import { config } from '@shopee-user/platform';
import { Locale, Env } from '@shopee-user/global';

export type SupportedLocale =
  | Locale.SG
  | Locale.TH
  | Locale.VN
  | Locale.ID
  | Locale.BR
  | Locale.PH
  | Locale.MY;

const { ENV, LOCALE } = config;

const AUTHENTICATE_HELP_LINK_MAP: Record<SupportedLocale, string> = {
  SG: 'https://seller.shopee.sg/edu/article/9326',
  VN:
    'https://spx.vn/support-center/faq?_app_id=sls-nss-main-vn&_offl_id=bundle',
  TH:
    'https://spx.co.th/support-center/faq?_app_id=sls-nss-main-th&_offl_id=bundle',
  ID: 'https://spx.co.id/webform/more-questions',
  BR: 'https://help.shopee.com.br/portal',
  PH:
    'https://spx.ph/customer-service-introduction?_app_id=sls-nss-main-ph&_offl_id=bundle',
  MY:
    'https://spx.com.my/customer-service-introduction?_app_id=sls-nss-main-my&_offl_id=bundle',
};

const SIGNUP_HELP_LINK_MAP: Record<SupportedLocale, string> = {
  SG: 'https://seller.shopee.sg/edu/article/9326',
  VN:
    'https://spx.vn/support-center/faq?_app_id=sls-nss-main-vn&_offl_id=bundle',
  TH:
    'https://spx.co.th/support-center/faq?_app_id=sls-nss-main-th&_offl_id=bundle',
  ID:
    'https://help.shopee.co.id/portal/article/73736-%3Cem%3EAgen%3C%2Fem%3E-%3Cem%3EShopee%3C%2Fem%3E?previousPage=search%20results%20page',
  BR: 'https://help.shopee.com.br/portal',
  PH:
    'https://spx.ph/customer-service-introduction?_app_id=sls-nss-main-ph&_offl_id=bundle',
  MY:
    'https://spx.com.my/customer-service-introduction?_app_id=sls-nss-main-my&_offl_id=bundle',
};

const RESET_PASSWORD_INFO_LINK_MAP: Record<SupportedLocale, string> = {
  SG: 'https://spx.sg',
  VN: 'https://spx.vn/support-center/faq',
  TH: 'https://spx.co.th/support-center/faq',
  ID:
    'https://help.shopee.co.id/portal/article/73736-%3Cem%3EAgen%3C%2Fem%3E-%3Cem%3EShopee%3C%2Fem%3E?previousPage=search%20results%20page',
  BR: 'https://help.shopee.com.br/portal',
  PH: 'https://spx.ph/customer-service-introduction',
  MY: 'https://spx.com.my/customer-service-introduction',
};

const AUTHENTICATE_CONTACT_US_LINK_MAP: Record<SupportedLocale, string> = {
  SG: 'https://spx.sg',
  VN: 'https://spx.vn/support-center/faq',
  TH: 'https://spx.co.th/support-center/faq',
  ID:
    'https://help.shopee.co.id/portal/article/73736-%3Cem%3EAgen%3C%2Fem%3E-%3Cem%3EShopee%3C%2Fem%3E?previousPage=search%20results%20page',
  BR: 'https://help.shopee.com.br/portal',
  PH: 'https://spx.ph/customer-service-introduction',
  MY: 'https://spx.com.my/customer-service-introduction',
};

const USER_ACCOUNT_CONTACT_US_LINK_MAP: Record<SupportedLocale, string> = {
  SG: 'https://seller.shopee.sg/edu/article/9326',
  VN: 'https://spx.vn/support-center/faq',
  TH: 'https://spx.co.th/support-center/faq',
  ID:
    'https://help.shopee.co.id/portal/article/73736-%3Cem%3EAgen%3C%2Fem%3E-%3Cem%3EShopee%3C%2Fem%3E?previousPage=search%20results%20page',
  BR: 'https://help.shopee.com.br/portal',
  PH: 'https://spx.ph/customer-service-introduction',
  MY: 'https://spx.com.my/customer-service-introduction',
};

const LOGIN_FRAUD_CONTACT_US_LINK_MAP: Record<SupportedLocale, string> = {
  SG: 'https://spx.sg',
  VN: 'https://spx.vn/support-center/faq',
  TH: 'https://spx.co.th/support-center/faq',
  ID:
    'https://help.shopee.co.id/portal/article/73736-%3Cem%3EAgen%3C%2Fem%3E-%3Cem%3EShopee%3C%2Fem%3E?previousPage=search%20results%20page',
  BR: 'https://help.shopee.com.br/portal',
  PH: 'https://spx.ph/customer-service-introduction',
  MY: 'https://spx.com.my/customer-service-introduction',
};

const CHANGE_PHONE_HELP_LINK_MAP: Record<SupportedLocale, string> = {
  SG: '',
  VN: '',
  TH: '',
  ID: '',
  BR: 'https://help.shopee.com.br/portal',
  PH: '',
  MY: 'https://spx.com.my/customer-service-introduction',
};

const TERMS_OF_SERVICE_LINK_MAP: {
  [key in Env]?: Record<SupportedLocale, string>;
} = {
  [Env.Test]: {
    [Locale.SG]:
      'https://spx.sg/downloads/resource/terms_of_service_policy_sg.pdf',
    [Locale.TH]:
      'https://test.spx.co.th/downloads/resource/terms_of_service_policy_th.pdf',
    [Locale.VN]:
      'https://test.spx.vn/downloads/resource/terms_of_service_policy_vn.pdf',
    [Locale.ID]:
      'https://spx.co.id/downloads/resource/terms_of_service_policy_id.pdf',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77113?previousPage=other%20articles',
    [Locale.PH]:
      'https://test.spx.ph/downloads/resource/terms_of_service_policy_ph.pdf',
    [Locale.MY]:
      'https://uat.spx.com.my/downloads/resource/terms_of_service_policy_my.pdf',
  },
  [Env.Uat]: {
    [Locale.SG]:
      'https://spx.sg/downloads/resource/terms_of_service_policy_sg.pdf',
    [Locale.TH]:
      'https://uat.spx.co.th/downloads/resource/terms_of_service_policy_th.pdf',
    [Locale.VN]:
      'https://uat.spx.vn/downloads/resource/terms_of_service_policy_vn.pdf',
    [Locale.ID]:
      'https://spx.co.id/downloads/resource/terms_of_service_policy_id.pdf',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77113?previousPage=other%20articles',
    [Locale.PH]:
      'https://uat.spx.ph/downloads/resource/terms_of_service_policy_ph.pdf',
    [Locale.MY]:
      'https://uat.spx.com.my/downloads/resource/terms_of_service_policy_my.pdf',
  },
  [Env.Staging]: {
    [Locale.SG]:
      'https://spx.sg/downloads/resource/terms_of_service_policy_sg.pdf',
    [Locale.TH]:
      'https://staging.spx.co.th/downloads/resource/terms_of_service_policy_th.pdf',
    [Locale.VN]:
      'https://staging.spx.vn/downloads/resource/terms_of_service_policy_vn.pdf',
    [Locale.ID]:
      'https://spx.co.id/downloads/resource/terms_of_service_policy_id.pdf',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77113?previousPage=other%20articles',
    [Locale.PH]:
      'https://staging.spx.ph/downloads/resource/terms_of_service_policy_ph.pdf',
    [Locale.MY]:
      'https://uat.spx.com.my/downloads/resource/terms_of_service_policy_my.pdf',
  },
  [Env.Live]: {
    [Locale.SG]:
      'https://spx.sg/downloads/resource/terms_of_service_policy_sg.pdf',
    [Locale.TH]:
      'https://spx.co.th/downloads/resource/terms_of_service_policy_th.pdf',
    [Locale.VN]:
      'https://spx.vn/downloads/resource/terms_of_service_policy_vn.pdf',
    [Locale.ID]:
      'https://spx.co.id/downloads/resource/terms_of_service_policy_id.pdf',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77113?previousPage=other%20articles',
    [Locale.PH]:
      'https://spx.ph/downloads/resource/terms_of_service_policy_ph.pdf',
    [Locale.MY]:
      'https://spx.com.my/downloads/resource/terms_of_service_policy_my.pdf',
  },
};

const TERMS_OF_SERVICE_LINK_WEBVIEW_MAP: {
  [key in Env]?: Record<SupportedLocale, string>;
} = {
  [Env.Test]: {
    [Locale.SG]: 'https://spx.sg/support-center/faq/category/1/article/2',
    [Locale.TH]:
      'https://uat.spx.co.th/support-center/faq/category/11/article/39',
    [Locale.VN]: 'https://uat.spx.vn/support-center/faq/category/6/article/61',
    [Locale.ID]: '',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77113?previousPage=other%20articles',
    [Locale.PH]: 'https://uat.spx.ph/support-center/faq/category/5/article/6',
    [Locale.MY]:
      'https://uat.spx.com.my/support-center/faq/category/14/article/39',
  },
  [Env.Uat]: {
    [Locale.SG]: 'https://spx.sg/support-center/faq/category/1/article/2',
    [Locale.TH]:
      'https://uat.spx.co.th/support-center/faq/category/11/article/39',
    [Locale.VN]: 'https://uat.spx.vn/support-center/faq/category/6/article/61',
    [Locale.ID]: '',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77113?previousPage=other%20articles',
    [Locale.PH]: 'https://uat.spx.ph/support-center/faq/category/5/article/6',
    [Locale.MY]:
      'https://uat.spx.com.my/support-center/faq/category/14/article/39',
  },
  [Env.Staging]: {
    [Locale.SG]: 'https://spx.sg/support-center/faq/category/1/article/2',
    [Locale.TH]:
      'https://uat.spx.co.th/support-center/faq/category/11/article/39',
    [Locale.VN]: 'https://uat.spx.vn/support-center/faq/category/6/article/61',
    [Locale.ID]: '',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77113?previousPage=other%20articles',
    [Locale.PH]: 'https://uat.spx.ph/support-center/faq/category/5/article/6',
    [Locale.MY]:
      'https://uat.spx.com.my/support-center/faq/category/14/article/39',
  },
  [Env.Live]: {
    [Locale.SG]: ' https://spx.sg/support-center/faq/category/1/article/2',
    [Locale.TH]:
      'https://spx.co.th/en/support-center/faq/category/7/article/52',
    [Locale.VN]: 'https://spx.vn/en/support-center/faq/category/13/article/70',
    [Locale.ID]: '',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77113?previousPage=other%20articles',
    [Locale.PH]: 'https://spx.ph/en/support-center/faq/category/1/article/1',
    [Locale.MY]: 'https://spx.com.my/support-center/faq/category/13/article/72',
  },
};

const PRIVACY_POLICY_LINK_MAP: {
  [key in Env]?: Record<SupportedLocale, string>;
} = {
  [Env.Test]: {
    [Locale.SG]: 'https://spx.sg/downloads/resource/privacy_policy_sg.pdf',
    [Locale.TH]:
      'https://test.spx.co.th/downloads/resource/privacy_policy_th.pdf',
    [Locale.VN]: 'https://test.spx.vn/downloads/resource/privacy_policy_vn.pdf',
    [Locale.ID]: 'https://spx.co.id/downloads/resource/privacy_policy_id.pdf',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77068?previousPage=other%20articles',
    [Locale.PH]: 'https://spx.ph/downloads/resource/privacy_policy_ph.pdf',
    [Locale.MY]:
      'https://uat.spx.com.my/downloads/resource/privacy_policy_my.pdf',
  },
  [Env.Uat]: {
    [Locale.SG]: 'https://spx.sg/downloads/resource/privacy_policy_sg.pdf',
    [Locale.TH]:
      'https://uat.spx.co.th/downloads/resource/privacy_policy_th.pdf',
    [Locale.VN]: 'https://uat.spx.vn/downloads/resource/privacy_policy_vn.pdf',
    [Locale.ID]: 'https://spx.co.id/downloads/resource/privacy_policy_id.pdf',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77068?previousPage=other%20articles',
    [Locale.PH]: 'https://spx.ph/downloads/resource/privacy_policy_ph.pdf',
    [Locale.MY]:
      'https://uat.spx.com.my/downloads/resource/privacy_policy_my.pdf',
  },
  [Env.Staging]: {
    [Locale.SG]: 'https://spx.sg/downloads/resource/privacy_policy_sg.pdf',
    [Locale.TH]:
      'https://staging.spx.co.th/downloads/resource/privacy_policy_th.pdf',
    [Locale.VN]:
      'https://staging.spx.vn/downloads/resource/privacy_policy_vn.pdf',
    [Locale.ID]: 'https://spx.co.id/downloads/resource/privacy_policy_id.pdf',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77068?previousPage=other%20articles',
    [Locale.PH]: 'https://spx.ph/downloads/resource/privacy_policy_ph.pdf',
    [Locale.MY]:
      'https://uat.spx.com.my/downloads/resource/privacy_policy_my.pdf',
  },
  [Env.Live]: {
    [Locale.SG]: 'https://spx.sg/downloads/resource/privacy_policy_sg.pdf',
    [Locale.TH]: 'https://spx.co.th/downloads/resource/privacy_policy_th.pdf',
    [Locale.VN]: 'https://spx.vn/downloads/resource/privacy_policy_vn.pdf',
    [Locale.ID]: 'https://spx.co.id/downloads/resource/privacy_policy_id.pdf',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77068?previousPage=other%20articles',
    [Locale.PH]: 'https://spx.ph/downloads/resource/privacy_policy_ph.pdf',
    [Locale.MY]: 'https://spx.com.my/downloads/resource/privacy_policy_my.pdf',
  },
};

const PRIVACY_POLICY_LINK_WEBVIEW_MAP: {
  [key in Env]?: Record<SupportedLocale, string>;
} = {
  [Env.Test]: {
    [Locale.SG]: 'https://spx.sg/support-center/faq/category/1/article/1',
    [Locale.TH]:
      'https://uat.spx.co.th/support-center/faq/category/11/article/38',
    [Locale.VN]: 'https://uat.spx.vn/support-center/faq/category/6/article/60',
    [Locale.ID]: '',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77068?previousPage=other%20articles',
    [Locale.PH]: 'https://uat.spx.ph/support-center/faq/category/5/article/6',
    [Locale.MY]:
      'https://uat.spx.com.my/support-center/faq/category/14/article/40',
  },
  [Env.Uat]: {
    [Locale.SG]: 'https://spx.sg/support-center/faq/category/1/article/1',
    [Locale.TH]:
      'https://uat.spx.co.th/support-center/faq/category/11/article/38',
    [Locale.VN]: 'https://uat.spx.vn/support-center/faq/category/6/article/60',
    [Locale.ID]: '',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77068?previousPage=other%20articles',
    [Locale.PH]: 'https://uat.spx.ph/support-center/faq/category/5/article/6',
    [Locale.MY]:
      'https://uat.spx.com.my/support-center/faq/category/14/article/40',
  },
  [Env.Staging]: {
    [Locale.SG]: 'https://spx.sg/support-center/faq/category/1/article/1',
    [Locale.TH]:
      'https://uat.spx.co.th/support-center/faq/category/11/article/38',
    [Locale.VN]: 'https://uat.spx.vn/support-center/faq/category/6/article/60',
    [Locale.ID]: '',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77068?previousPage=other%20articles',
    [Locale.PH]: 'https://uat.spx.ph/support-center/faq/category/5/article/6',
    [Locale.MY]:
      'https://uat.spx.com.my/support-center/faq/category/14/article/40',
  },
  [Env.Live]: {
    [Locale.SG]: 'https://spx.sg/support-center/faq/category/1/article/1',
    [Locale.TH]:
      'https://spx.co.th/en/support-center/faq/category/7/article/52',
    [Locale.VN]: 'https://spx.vn/en/support-center/faq/category/13/article/71',
    [Locale.ID]: '',
    [Locale.BR]:
      'https://help.shopee.com.br/portal/article/77068?previousPage=other%20articles',
    [Locale.PH]: 'https://spx.ph/en/support-center/faq/category/1/article/1',
    [Locale.MY]: 'https://spx.com.my/support-center/faq/category/13/article/71',
  },
};

const PHONE_OR_EMAIL_LINKED_CONTACT_US_LINK_MAP: Record<
  SupportedLocale,
  string
> = {
  SG: 'https://seller.shopee.sg/edu/article/9326',
  VN: 'https://help.shopee.vn/portal/webform/4d8d2e07487345cf9a9881fde841079c',
  TH: '',
  ID: 'https://help.shopee.co.id/portal/article/71884',
  BR: 'https://help.shopee.com.br/portal',
  PH: '',
  MY: 'https://spx.com.my/customer-service-introduction',
};

const TOC_ACCOUNT_CONTACT_US_LINK_MAP: Record<SupportedLocale, string> = {
  SG: 'https://seller.shopee.sg/edu/article/9326',
  VN: 'https://help.shopee.vn/portal/webform/4d8d2e07487345cf9a9881fde841079c',
  TH: '',
  ID: 'https://help.shopee.co.id/portal/article/71884',
  BR: 'https://help.shopee.com.br/portal',
  PH: '',
  MY: 'https://spx.com.my/customer-service-introduction',
};

export const AUTHENTICATE_HELP_LINK =
  AUTHENTICATE_HELP_LINK_MAP[LOCALE as SupportedLocale] || '';
export const ONE_CREDENTIAL_RECLAIM_PHONE_FAILED_HELP_LINK = AUTHENTICATE_HELP_LINK;
export const SIGNUP_HELP_LINK =
  SIGNUP_HELP_LINK_MAP[LOCALE as SupportedLocale] || '';
export const ACCOUNT_SUSPENDED_HELP_LINK = AUTHENTICATE_HELP_LINK;
export const RESET_PASSWORD_INFO_LINK =
  RESET_PASSWORD_INFO_LINK_MAP[LOCALE as SupportedLocale] || '';
export const AUTHENTICATE_CONTACT_US_LINK =
  AUTHENTICATE_CONTACT_US_LINK_MAP[LOCALE as SupportedLocale] || '';
export const RESET_PASSWORD_CONTACT_US_LINK = '';
export const USER_ACCOUNT_CONTACT_US_LINK =
  USER_ACCOUNT_CONTACT_US_LINK_MAP[LOCALE as SupportedLocale] || '';
export const LOGIN_FRAUD_CONTACT_US_LINK =
  LOGIN_FRAUD_CONTACT_US_LINK_MAP[LOCALE as SupportedLocale] || '';
export const CHANGE_PHONE_HELP_LINK =
  CHANGE_PHONE_HELP_LINK_MAP[config.LOCALE as SupportedLocale] || '';
export const PHONE_OR_EMAIL_LINKED_CONTACT_US_LINK =
  PHONE_OR_EMAIL_LINKED_CONTACT_US_LINK_MAP[config.LOCALE as SupportedLocale] ||
  '';
export const TOC_ACCOUNT_CONTACT_US_LINK =
  TOC_ACCOUNT_CONTACT_US_LINK_MAP[config.LOCALE as SupportedLocale];
export const ACCOUNT_DELETE_IOS_LINK = '';
export const ACCOUNT_DELETE_ANDROID_LINK = '';
export const CHANGE_PHONE_NATIVE_VERIFICATION_LINK = '';

export const TERMS_OF_SERVICE_LINK =
  TERMS_OF_SERVICE_LINK_MAP[ENV] &&
  (TERMS_OF_SERVICE_LINK_MAP[ENV] as Record<SupportedLocale, string>)[
    LOCALE as SupportedLocale
  ]
    ? ((TERMS_OF_SERVICE_LINK_MAP[ENV] as Record<SupportedLocale, string>)[
        LOCALE as SupportedLocale
      ] as string)
    : '';
export const PRIVACY_POLICY_LINK =
  PRIVACY_POLICY_LINK_MAP[ENV] &&
  (PRIVACY_POLICY_LINK_MAP[ENV] as Record<SupportedLocale, string>)[
    LOCALE as SupportedLocale
  ]
    ? ((PRIVACY_POLICY_LINK_MAP[ENV] as Record<SupportedLocale, string>)[
        LOCALE as SupportedLocale
      ] as string)
    : '';
export const TERMS_OF_SERVICE_WEBVIEW_LINK =
  TERMS_OF_SERVICE_LINK_WEBVIEW_MAP[ENV] &&
  (TERMS_OF_SERVICE_LINK_WEBVIEW_MAP[ENV] as Record<SupportedLocale, string>)[
    LOCALE as SupportedLocale
  ]
    ? ((TERMS_OF_SERVICE_LINK_WEBVIEW_MAP[ENV] as Record<
        SupportedLocale,
        string
      >)[LOCALE as SupportedLocale] as string)
    : '';
export const PRIVACY_POLICY_WEBVIEW_LINK =
  PRIVACY_POLICY_LINK_WEBVIEW_MAP[ENV] &&
  (PRIVACY_POLICY_LINK_WEBVIEW_MAP[ENV] as Record<SupportedLocale, string>)[
    LOCALE as SupportedLocale
  ]
    ? ((PRIVACY_POLICY_LINK_WEBVIEW_MAP[ENV] as Record<
        SupportedLocale,
        string
      >)[LOCALE as SupportedLocale] as string)
    : '';
export const COOKIE_POLICY_LINK = '';
export const DATA_PROTECTION_POLICY_LINK = '';
export const PLATFORM_PARTNER_RULES_LINK = '';
